import React, { useState, useRef, useEffect, useCallback } from "react";
import { useUI } from "../../context/UIContext";
import { getPm2Logs } from "../../api";

const LINE_OPTS = [200, 500, 1000, 2000];

export default function Pm2LogModal({ serverCode, name }) {
  const { closeModal, toast } = useUI();
  const [lines, setLines] = useState(500);
  const [tab, setTab] = useState("all"); // 'all' | 'out' | 'error'
  const [data, setData] = useState({ out: [], error: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stamp, setStamp] = useState(null);
  const outRef = useRef(null);

  const load = useCallback(
    async (n) => {
      setBusy(true);
      setErr("");
      try {
        const res = await getPm2Logs(serverCode, name, n);
        setData({ out: res.out || [], error: res.error || [] });
        setStamp(new Date());
      } catch (e) {
        setErr(e.message || "Không lấy được log");
      } finally {
        setBusy(false);
      }
    },
    [serverCode, name]
  );

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    load(lines);
  }, [load, lines]);

  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [data, tab, busy]);

  const onLines = (n) => {
    setLines(n);
    load(n);
  };

  const merged = [
    ...data.out.map((t) => ({ t, e: false })),
    ...data.error.map((t) => ({ t, e: true })),
  ];
  const view =
    tab === "out"
      ? data.out.map((t) => ({ t, e: false }))
      : tab === "error"
      ? data.error.map((t) => ({ t, e: true }))
      : merged;

  const plain = view.map((l) => l.t).join("\n");

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(plain);
      toast("Đã copy nội dung console ✅", "ok");
    } catch {
      toast("Trình duyệt chặn copy", "warn");
    }
  };

  const download = () => {
    const blob = new Blob([plain], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = (stamp || new Date()).toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `${serverCode}_${name}_${tab}_${ts}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const TabBtn = ({ value, label, count }) => {
    const isAct = tab === value;
    return (
      <button
        onClick={() => setTab(value)}
        style={{
          padding: "5px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 600,
          cursor: "pointer",
          border: isAct ? "none" : "1px solid var(--border2)",
          background: isAct ? "var(--primary)" : "transparent",
          color: isAct ? "#fff" : "var(--dim)",
          transition: "all 0.15s ease",
        }}
        className="log-tab-btn"
      >
        {label}
        {count != null && <span style={{ opacity: 0.7 }}> ({count})</span>}
      </button>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      {/* Title and Tabs Header */}
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          fontSize: "12px",
          color: "var(--dim)",
        }}
      >
        <span>
          Console: <strong style={{ color: "var(--text)" }}>{name}</strong> @ {serverCode}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
          <TabBtn value="all" label="Tất cả" count={merged.length} />
          <TabBtn value="out" label="Output" count={data.out.length} />
          <TabBtn value="error" label="Lỗi" count={data.error.length} />
        </div>
      </div>

      {/* Terminal logs window */}
      <div
        className="terminal-window scroll"
        ref={outRef}
        style={{ height: "420px", maxHeight: "55vh" }}
      >
        {busy && view.length === 0 ? (
          <div style={{ color: "var(--amber)", fontFamily: "var(--font-mono)" }}>Đang lấy log…</div>
        ) : err ? (
          <div style={{ color: "var(--red)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)" }}>
            ✕ {err}
          </div>
        ) : view.length === 0 ? (
          <div style={{ color: "var(--faint)", fontFamily: "var(--font-mono)" }}>
            (Không có dòng log nào — app chưa in ra console hoặc PM2 chưa ghi file log)
          </div>
        ) : (
          view.map((l, i) => (
            <div
              key={i}
              style={{
                whiteSpace: "pre-wrap",
                color: l.e ? "var(--red)" : "var(--text)",
                fontFamily: "var(--font-mono)",
                fontSize: "12.5px",
                lineHeight: "1.5",
              }}
            >
              {l.t || " "}
            </div>
          ))
        )}
      </div>

      {/* Lines selection and Date Stamp */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "12px",
          fontSize: "12px",
          color: "var(--faint)",
          fontFamily: "var(--font-mono)",
          flexWrap: "wrap",
        }}
      >
        <span>Số dòng:</span>
        {LINE_OPTS.map((n) => {
          const isAct = lines === n;
          return (
            <button
              key={n}
              onClick={() => onLines(n)}
              disabled={busy}
              style={{
                padding: "3px 8px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                border: isAct ? "none" : "1px solid var(--border2)",
                background: isAct ? "var(--primary)" : "transparent",
                color: isAct ? "#fff" : "var(--dim)",
                transition: "all 0.15s ease",
              }}
              className="line-opt-btn"
            >
              {n}
            </button>
          );
        })}
        {stamp && (
          <span style={{ marginLeft: "auto" }}>
            Chụp lúc {stamp.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Footer controls */}
      <div className="modal-footer" style={{ marginTop: "20px" }}>
        <button
          className="mbtn mbtn-primary"
          onClick={() => load(lines)}
          disabled={busy}
        >
          {busy ? "…" : "↻ Chụp lại"}
        </button>
        <button className="mbtn mbtn-cancel" onClick={copyAll} disabled={busy}>
          Copy
        </button>
        <button className="mbtn mbtn-cancel" onClick={download} disabled={busy}>
          Tải .log
        </button>
        <button className="mbtn mbtn-cancel" onClick={closeModal}>
          Đóng
        </button>
      </div>
      
      <style>{`
        .log-tab-btn:hover:not(:disabled) {
          border-color: var(--dim) !important;
        }
        .line-opt-btn:hover:not(:disabled) {
          border-color: var(--dim) !important;
        }
      `}</style>
    </div>
  );
}
